import { AccountType, FeatureFlag, InquiryOfferState, UserRole } from '@synergy/models';
import { Box } from '@mui/material';
import {
    EmailOutlined,
    LogoutOutlined,
    MoveToInboxOutlined,
    PaymentsOutlined,
    PeopleAltOutlined,
    QuestionAnswerOutlined,
    StorefrontOutlined,
} from '@mui/icons-material';
import { Notifier } from '@synergy/shared/snackbar/feature';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { PaloChrome, PaloSection, paloInitials, paloSignOutHref } from '@synergy/components-palo';
import { ReactElement } from 'react';
import { SHOP_URL } from '@synergy/config';
import { getDocumentStatusTranslationId, hasFeature, hasRole } from '@synergy/utils';
import { useIntl } from 'react-intl';

import { ROUTES } from './features/sidebar-menu/navigation-drawer/routes';
import { protectedComponents as pc } from './protected-components';
import { useOpenInquiryCount } from './features/inquiry/palo/use-open-inquiry-count';
import { useSelector } from './store/use-appstate-selector';
import paloLogo from './common/style/palo-logo.svg';

/** Rail key of the shop, which leaves the app rather than routing inside it. */
const SHOP_KEY = 'shop';

/**
 * Page size and page, the way the drawer's links carried them.
 *
 * The lists apply their defaults on reset, not on first load, so a link that
 * drops these hands the backend an empty query and the list comes back in a
 * different order than the one people know.
 */
const LIST_DEFAULTS = 'limit=50&page=1';

const listUrl = (base: string): string => `${base}?${LIST_DEFAULTS}`;

const byState = (base: string, state: InquiryOfferState): string =>
    `${base}?${LIST_DEFAULTS}&filter[inquiryOfferStates]=${state}`;

/**
 * Where the portal opens: the inquiries still waiting for an offer, the
 * nearest deadline first (R-257).
 */
export const OPEN_INQUIRIES = `${byState(ROUTES.inquires, InquiryOfferState.Open)}&sort=endDate&order=asc`;

/**
 * The PALO shell for the supplier portal.
 *
 * Mirrors the admin's layout, with two differences that come from who is on the
 * other side: every label goes through react-intl, because suppliers read this
 * in four languages, and the rail is built from the signed-in user's roles and
 * the active feature flags, exactly as the drawer it replaces was.
 */
export const PaloAppLayout = (): ReactElement => {
    const intl = useIntl();
    const location = useLocation();
    const navigate = useNavigate();
    const user = useSelector(state => state.user);
    const fullName = useSelector(state => state.user?.fullName ?? '');
    const isImpersonator = useSelector(state => !!state.settings?.isImpersonator);
    // The portal admits anyone holding a supplier role, and a few of them sit
    // on customer accounts. The sections below query supplier data, which the
    // backend rightly refuses for such an account — so do not offer them.
    const isSupplierAccount = useSelector(state => AccountType.Supplier === state.currentAccount?.type);

    const openInquiries = useOpenInquiryCount();

    const t = (id: string): string => intl.formatMessage({ id });

    const sections: PaloSection[] = [
        {
            key: 'inquiries',
            label: t('app.inquiries.overview'),
            icon: <QuestionAnswerOutlined />,
            to: OPEN_INQUIRIES,
            navTitle: t('app.inquiries.overview'),
            groups: [
                {
                    label: t('app.navigation.group.current'),
                    items: [
                        {
                            label: t(getDocumentStatusTranslationId(InquiryOfferState.Open)),
                            to: OPEN_INQUIRIES,
                            count: openInquiries,
                        },
                        {
                            label: t(getDocumentStatusTranslationId(InquiryOfferState.OnGoing)),
                            to: byState(ROUTES.inquires, InquiryOfferState.OnGoing),
                        },
                        { label: t('app.filter.all'), to: listUrl(ROUTES.inquires) },
                    ],
                },
                {
                    label: t('app.navigation.group.archive'),
                    items: [
                        {
                            label: t(getDocumentStatusTranslationId(InquiryOfferState.Closed)),
                            to: byState(ROUTES.inquires, InquiryOfferState.Closed),
                        },
                        {
                            label: t(getDocumentStatusTranslationId(InquiryOfferState.Declined)),
                            to: byState(ROUTES.inquires, InquiryOfferState.Declined),
                        },
                    ],
                },
            ],
        },
        ...(hasFeature(FeatureFlag.PurchaseOrders)
            ? [
                  {
                      key: 'purchases',
                      label: t('app.navigation.purchases'),
                      icon: <MoveToInboxOutlined />,
                      to: listUrl(ROUTES.purchases),
                      navTitle: t('app.navigation.purchases'),
                      groups: [
                          {
                              label: t('app.navigation.group.overview'),
                              items: [{ label: t('app.filter.all'), to: listUrl(ROUTES.purchases) }],
                          },
                      ],
                  },
              ]
            : []),
        ...(isSupplierAccount &&
        hasRole([UserRole.SupplierAdmin, UserRole.SupplierBonusContact], user) &&
        hasFeature(FeatureFlag.BonusSettlements)
            ? [
                  {
                      key: 'bonusSettlements',
                      label: t('app.navigation.bonusSettlements'),
                      icon: <PaymentsOutlined />,
                      to: listUrl(ROUTES.bonusSettlements.overview),
                      navTitle: t('app.navigation.bonusSettlements'),
                      groups: [
                          {
                              label: t('app.navigation.group.overview'),
                              items: [{ label: t('app.filter.all'), to: listUrl(ROUTES.bonusSettlements.overview) }],
                          },
                      ],
                  },
              ]
            : []),
        ...(isSupplierAccount && hasRole(UserRole.SupplierAdmin, user)
            ? [
                  {
                      key: 'userManagement',
                      label: t('app.navigation.userManagement'),
                      icon: <PeopleAltOutlined />,
                      to: listUrl(ROUTES.userManagement),
                      navTitle: t('app.navigation.userManagement'),
                      groups: [
                          {
                              label: t('app.navigation.group.directory'),
                              items: [{ label: t('app.filter.all'), to: listUrl(ROUTES.userManagement) }],
                          },
                      ],
                  },
              ]
            : []),
        ...(hasRole(UserRole.SupplierAdmin, user)
            ? [
                  {
                      key: 'notifications',
                      label: t('app.navigation.notifications'),
                      icon: <EmailOutlined />,
                      to: ROUTES.notifications,
                      navTitle: t('app.navigation.notifications'),
                      groups: [
                          {
                              label: t('app.navigation.group.settings'),
                              items: [{ label: t('app.filter.all'), to: ROUTES.notifications }],
                          },
                      ],
                  },
              ]
            : []),
        {
            key: SHOP_KEY,
            label: t('app.navigation.linkToShop'),
            icon: <StorefrontOutlined />,
            to: SHOP_URL,
            navTitle: t('app.navigation.linkToShop'),
            groups: [],
        },
    ];

    /** Pathname prefixes that map a route onto its rail section. */
    const sectionMatchers: { key: string; prefixes: string[] }[] = [
        { key: 'inquiries', prefixes: [ROUTES.inquires] },
        { key: 'purchases', prefixes: [ROUTES.purchases] },
        { key: 'bonusSettlements', prefixes: [ROUTES.bonusSettlements.overview] },
        { key: 'userManagement', prefixes: [ROUTES.userManagement] },
        { key: 'notifications', prefixes: [ROUTES.notifications] },
    ];

    const activeSectionKey =
        sectionMatchers.find(matcher =>
            matcher.prefixes.some(prefix => location.pathname === prefix || location.pathname.startsWith(prefix + '/'))
        )?.key ?? 'inquiries';

    const handleNavigate = (to: string): void => {
        // The shop is a separate application on its own host; routing to it
        // would leave the router looking for a path that does not exist here.
        if (/^https?:\/\//.test(to)) {
            window.location.assign(to);

            return;
        }

        navigate(to);
    };

    return (
        <>
            <PaloChrome
                sections={sections}
                activeSectionKey={activeSectionKey}
                activePath={location.pathname + location.search}
                onNavigate={handleNavigate}
                userName={fullName}
                userInitials={paloInitials(fullName)}
                userMenuItems={[
                    {
                        label: t(isImpersonator ? 'app.navigation.exitImpersonator' : 'app.navigation.logout'),
                        href: paloSignOutHref(isImpersonator),
                        icon: <LogoutOutlined />,
                    },
                ]}
                logo={
                    <Box sx={{ textAlign: 'center' }}>
                        <img src={paloLogo} alt="PALO" style={{ width: 116, height: 'auto', display: 'block' }} />
                        <Box
                            sx={{
                                fontSize: '9.5px',
                                fontWeight: 600,
                                letterSpacing: '.09em',
                                textTransform: 'uppercase',
                                color: '#6C7786',
                                marginTop: '3px',
                            }}
                        >
                            supplier
                        </Box>
                    </Box>
                }
            >
                <pc.UserManagementReviewPopupRestricted />
                <Outlet />
            </PaloChrome>
            <Notifier />
        </>
    );
};
